import { useState, type FormEvent, type ReactNode } from 'react';
import { FileSearch, ListTree, RefreshCw, Star, Trash2 } from 'lucide-react';
import { Button, Card, EmptyState, Fact, Facts, HttpStatus, IconButton, Input } from '@/components/ui';
import { useAddKeyPage, useRemoveKeyPage } from '@/hooks/useSeo';
import { toast } from '@/store/toastStore';
import { errorText } from '@/utils/errors';
import { SEO_LEVEL } from '@/utils/seo';
import type { SiteDetail, SiteSeoView } from '@/types/api';

/** A page's path as people read it: `/2026/09/沖縄の経済…`, not its percent-encoding. */
function readablePath(address: string): string {
  try {
    const url = new URL(address);
    return decodeURIComponent(`${url.pathname}${url.search}`);
  } catch {
    return address;
  }
}

/**
 * What the crawler-like read found (ADR-0045), on a tab of its own
 * (ADR-0057): the front page as search engines read it, robots.txt and the
 * sitemap with a sample of its pages, the pages the operator watches, and the
 * last checks. Every HTTP answer is a badge: green, blue, amber, red.
 */
export function SeoTechnical({ site, view, mayEdit }: { readonly site: SiteDetail; readonly view: SiteSeoView; readonly mayEdit: boolean }): ReactNode {
  const [path, setPath] = useState('');
  const add = useAddKeyPage(site.id, { onSuccess: () => setPath('') });
  const remove = useRemoveKeyPage(site.id, { onError: (error) => toast.error('Не удалось убрать страницу', errorText(error)) });
  const facts = view.facts;
  const home = facts?.home;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (path.trim()) add.mutate(path.trim());
  };

  return (
    <div className="stack">
      {facts && home ? (
        <div className="split">
          <Card title="Главная страница" icon={FileSearch}>
            <Facts>
              <Fact label="Ответ">
                <span className="row-tight row-wrap">
                  <HttpStatus code={home.status} />
                  {home.redirects.length > 0 ? <span className="muted">через {home.redirects.length} перенаправл.</span> : null}
                </span>
              </Fact>
              <Fact label="title">{home.title ? <span>{home.title} <span className="muted">· {home.title.length}</span></span> : <span className="faint">нет</span>}</Fact>
              <Fact label="description">
                {home.description ? <span>{home.description} <span className="muted">· {home.description.length}</span></span> : <span className="faint">нет</span>}
              </Fact>
              <Fact label="H1">{home.h1}</Fact>
              <Fact label="canonical">{home.canonical ? <span className="mono">{home.canonical}</span> : <span className="faint">нет</span>}</Fact>
              <Fact label="http → https">
                {facts.plainHttp?.finalUrl?.startsWith('https://') ? (
                  <span className="tone-healthy">переводит</span>
                ) : (
                  <span className="tone-degraded">не переводит{facts.plainHttp?.finalUrl ? ` (${facts.plainHttp.finalUrl})` : ''}</span>
                )}
              </Fact>
              {home.hreflang.length > 0 ? (
                <Fact label="hreflang">
                  <ul className="list-plain">
                    {home.hreflang.map((link) => (
                      <li key={`${link.lang}-${link.href}`}>
                        <span className="mono">{link.lang}</span> → <span className="mono">{link.href}</span>
                      </li>
                    ))}
                  </ul>
                </Fact>
              ) : null}
              <Fact label="Счётчики">
                {home.counters.length > 0 ? home.counters.map((counter) => `${counter.name}${counter.id ? ` ${counter.id}` : ''}`).join(', ') : <span className="faint">не найдены</span>}
              </Fact>
              <Fact label="Подтверждения">
                {home.verification.length > 0 ? home.verification.map((item) => item.engine).join(', ') : <span className="faint">не найдены</span>}
              </Fact>
            </Facts>
          </Card>

          <Card title="robots.txt и sitemap" icon={ListTree}>
            <Facts>
              <Fact label="robots.txt">
                <span className="row-tight row-wrap">
                  <HttpStatus code={facts.robots.status} />
                  {facts.robots.status === 200 ? (
                    facts.robots.blocksAll ? (
                      <span className="tone-down">закрывает сайт целиком</span>
                    ) : (
                      <span>не мешает индексации</span>
                    )
                  ) : (
                    <span className="muted">файла нет</span>
                  )}
                </span>
              </Fact>
              <Fact label="Sitemap">{facts.sitemap.url ? <span className="mono">{facts.sitemap.url}</span> : <span className="faint">не найден</span>}</Fact>
              {facts.sitemap.urls !== null ? (
                <Fact label="Страниц в нём">
                  {facts.sitemap.urls.toLocaleString('ru-RU')}
                  {facts.sitemap.partial ? <span className="muted"> · в первых файлах индекса</span> : null}
                  {facts.sitemap.foreign > 0 ? <span className="muted"> · на чужом домене: {facts.sitemap.foreign}</span> : null}
                </Fact>
              ) : null}
              {facts.sitemap.sampled.length > 0 ? (
                <Fact label="Выборка страниц">
                  <ul className="list-plain">
                    {facts.sitemap.sampled.map((page) => (
                      <li key={page.url} className="row-between seo-sample">
                        <a className="truncate" href={page.url} target="_blank" rel="noreferrer noopener" title={page.url}>
                          {readablePath(page.url)}
                        </a>
                        <HttpStatus code={page.status} />
                      </li>
                    ))}
                  </ul>
                </Fact>
              ) : null}
            </Facts>
          </Card>
        </div>
      ) : (
        <Card>
          <EmptyState icon={FileSearch} message="Проверок ещё не было" />
        </Card>
      )}

      <Card flush title="Ключевые страницы" icon={Star}>
        {view.keyPages.length === 0 ? (
          <EmptyState icon={Star} message="Ключевых страниц нет. Добавьте до 30 страниц для ежедневной проверки" />
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th scope="col">Страница</th>
                  <th scope="col">Ответ</th>
                  <th scope="col">title</th>
                  <th scope="col">Индексация</th>
                  <th scope="col" className="shrink" />
                </tr>
              </thead>
              <tbody>
                {view.keyPages.map((page) => {
                  const seen = facts?.keyPages.find((item) => item.url.endsWith(page.path));
                  return (
                    <tr key={page.id}>
                      <td className="mono">{page.path}</td>
                      <td>{seen ? <HttpStatus code={seen.status} /> : <span className="faint">при следующей проверке</span>}</td>
                      <td>{seen?.title ?? <span className="faint">—</span>}</td>
                      <td>{seen ? seen.noindex ? <span className="tone-down">noindex</span> : 'открыта' : '—'}</td>
                      <td className="shrink">
                        {mayEdit ? <IconButton icon={Trash2} size="sm" variant="ghost" label="Убрать из ключевых" onClick={() => remove.mutate(page.id)} /> : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {mayEdit ? (
          <form className="row card-foot" onSubmit={submit}>
            <Input
              aria-label="Страница"
              className="mono"
              value={path}
              onChange={(event) => setPath(event.target.value)}
              placeholder={`/catalog или https://${site.primaryDomain ?? 'example.com'}/catalog`}
              spellCheck={false}
            />
            <Button type="submit" size="sm" busy={add.isPending} disabled={!path.trim()}>
              Добавить
            </Button>
            {add.isError ? <span className="field-error">{errorText(add.error)}</span> : null}
          </form>
        ) : null}
      </Card>

      {view.history.length > 1 ? (
        <Card title="Последние проверки" icon={RefreshCw}>
          <div className="seo-history" aria-label="Результаты последних проверок">
            {view.history.map((look) => (
              <span
                key={look.takenAt}
                className={`seo-dot seo-dot-${look.level}`}
                title={`${new Date(look.takenAt).toLocaleString('ru-RU')}: ${SEO_LEVEL[look.level]?.label ?? look.level}${look.sitemapUrls !== null ? `, в sitemap ${look.sitemapUrls}` : ''}`}
              />
            ))}
          </div>
        </Card>
      ) : null}
    </div>
  );
}
